import { api } from './client';
import type { Platform } from './types';

// ============================================
// Auth Account Types
// ============================================

export interface PlatformAuthAccount {
  id: string;
  platform: Platform;
  account_id: string;
  account_name: string;
  status: string;
  token_expiry?: string;
  metadata?: unknown;
  created_at: string;
  updated_at?: string;
}

export interface AuthAccountListResponse {
  data: PlatformAuthAccount[];
  total: number;
  page: number;
  size: number;
}

export interface ListAuthAccountsParams {
  platform?: Platform;
  status?: string;
  page?: number;
  size?: number;
}

// ============================================
// Platform Account (simplified) Types
// ============================================

export interface PlatformAccountSimple {
  id: string;
  user_id: string;
  user_name: string;
  brand_id: string;
  brand_name: string;
  site_id: string;
  site_name: string;
  platform: string;
  account_name: string;
  account_id: string;
  auth_account_id: string;
  status: string;
}

export interface PlatformAccountSimpleListResponse {
  data: PlatformAccountSimple[];
  total: number;
  page: number;
  size: number;
}

export interface ListPlatformAccountsParams {
  platform?: string;
  brand_id?: string;
  site_id?: string;
  user_id?: string;
  scope?: 'campaign_read' | 'campaign_write' | 'campaign_copy' | 'dashboard_read' | 'dashboard_export' | 'platform_account_read' | 'platform_account_manage';
  page?: number;
  size?: number;
}

export interface CreatePlatformAccountRequest {
  user_id: string;
  brand_id: string;
  site_id: string;
  platform: string;
  auth_account_id: string;
}

export interface UpdatePlatformAccountRequest {
  user_id: string;
  brand_id: string;
  site_id: string;
  auth_account_id: string;
  status: string;
}

// ============================================
// API functions
// ============================================

export const authAccountsApi = {
  // Auth account management
  list: (params?: ListAuthAccountsParams) =>
    api.get<AuthAccountListResponse>('/auth-accounts', { params }),

  refresh: (id: string) =>
    api.post<{ success: boolean; new_expiry?: string; error?: string }>(`/auth-accounts/${id}/refresh`),

  delete: (id: string) =>
    api.delete<void>(`/auth-accounts/${id}`),

  // Platform account management
  listPlatformAccounts: (params?: ListPlatformAccountsParams) =>
    api.get<PlatformAccountSimpleListResponse>('/platform-accounts', { params }),

  createPlatformAccount: (data: CreatePlatformAccountRequest) =>
    api.post<unknown>('/platform-accounts', data),

  updatePlatformAccount: (id: string, data: UpdatePlatformAccountRequest) =>
    api.put<void>(`/platform-accounts/${id}`, data),

  deletePlatformAccount: (id: string) =>
    api.delete<void>(`/platform-accounts/${id}`),

  listUserOptions: (brandID: string) =>
    api.get<{ list: { user_id: string; user_name: string }[] }>('/platform-accounts/user-options', {
      params: { brand_id: brandID },
    }),
};
